import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Encabezado, Insignia } from '../componentes/ui/basicos';
import { Campo, Selector } from '../componentes/ui/formulario';
import { SeccionDeRecurso } from '../componentes/ui/seccion-de-recurso';
import type { Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { Boton } from '../componentes/ui/basicos';
import { CorregirNombre, type Corregible } from '../componentes/CorregirNombre';
import { ListaAsignada } from '../componentes/ListaAsignada';

interface Sucursal {
  readonly id: string;
  readonly nombre: string;
}

interface Deposito {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly sucursalId: string;
  readonly esPredeterminado: boolean;
  readonly activo: boolean;
}

interface PuestoDeCaja {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly sucursalId: string;
  readonly activo: boolean;
  /** La lista con la que vende esta caja, si tiene una propia (E36). */
  readonly listaDePreciosId: string | null;
}

interface PuntoDeVenta {
  readonly id: string;
  readonly numero: number;
  readonly nombre: string;
  readonly sucursalId: string;
  readonly modo: string;
  readonly activo: boolean;
}

/**
 * Depósitos, puestos de caja y puntos de venta.
 *
 * Van juntos porque los tres cuelgan de una sucursal y se configuran de una sentada
 * cuando se abre un local. Separarlos en tres pantallas obligaría a elegir la misma
 * sucursal tres veces.
 */
export function Estructura(): React.ReactElement {
  const { t } = useTranslation();
  // Corregir un puesto de caja pide el permiso de depósitos desde E15: los tres se editan juntos.
  const puedeEditarCajas = usePermiso('EMPRESA.DEPOSITO.EDITAR');

  const [depositoNuevo, setDepositoNuevo] = useState({
    codigo: '',
    nombre: '',
    sucursalId: '',
    esPredeterminado: false,
  });
  const [cajaNueva, setCajaNueva] = useState({ codigo: '', nombre: '', sucursalId: '' });
  const [puntoNuevo, setPuntoNuevo] = useState({ numero: '', nombre: '', sucursalId: '' });

  /** Qué se está corrigiendo y por qué ruta. Nulo con el diálogo cerrado. */
  const [corrigiendo, setCorrigiendo] = useState<{
    que: Corregible;
    ruta: string;
    clave: string;
    conCodigo: boolean;
  } | null>(null);

  const columnaDeCorregir = <T extends Corregible>(
    ruta: string,
    clave: string,
    conCodigo = true,
  ): Columna<T> => ({
    clave: 'acciones',
    titulo: '',
    contenido: (fila) => (
      <div className="flex justify-end">
        <Boton
          onClick={() => {
            setCorrigiendo({ que: fila, ruta, clave, conCodigo });
          }}
        >
          {t('acciones.corregir')}
        </Boton>
      </div>
    ),
  });

  const sucursales = useQuery({
    queryKey: ['sucursales'],
    queryFn: () => pedir<Sucursal[]>('/sucursales'),
  });

  const nombreDeSucursal = (id: string): string =>
    sucursales.data?.find((sucursal) => sucursal.id === id)?.nombre ?? '—';

  const opcionesDeSucursal = (
    <>
      <option value="" disabled>
        {t('estructura.elegirSucursal')}
      </option>
      {(sucursales.data ?? []).map((sucursal) => (
        <option key={sucursal.id} value={sucursal.id}>
          {sucursal.nombre}
        </option>
      ))}
    </>
  );

  const columnaDeSucursal = <T extends { sucursalId: string }>(): Columna<T> => ({
    clave: 'sucursal',
    titulo: t('estructura.sucursal'),
    contenido: (fila) => nombreDeSucursal(fila.sucursalId),
  });

  const columnasDeDeposito: Columna<Deposito>[] = [
    {
      clave: 'codigo',
      titulo: t('estructura.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    { clave: 'nombre', titulo: t('estructura.nombre'), contenido: (fila) => fila.nombre },
    columnaDeSucursal<Deposito>(),
    {
      clave: 'predeterminado',
      titulo: t('estructura.predeterminado'),
      contenido: (fila) =>
        fila.esPredeterminado ? <Insignia tono="bien">{t('estructura.si')}</Insignia> : null,
    },
    columnaDeCorregir<Deposito>('/depositos', 'depositos', true),
  ];

  const columnasDeCaja: Columna<PuestoDeCaja>[] = [
    {
      clave: 'codigo',
      titulo: t('estructura.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    { clave: 'nombre', titulo: t('estructura.nombre'), contenido: (fila) => fila.nombre },
    columnaDeSucursal<PuestoDeCaja>(),
    {
      clave: 'lista',
      titulo: t('listasDePrecios.asignada'),
      contenido: (fila) => (
        <ListaAsignada
          valor={fila.listaDePreciosId}
          ruta={`/puestos-de-caja/${fila.id}`}
          clave="puestos-de-caja"
          puedeEditar={puedeEditarCajas}
        />
      ),
    },
    columnaDeCorregir<PuestoDeCaja>('/puestos-de-caja', 'puestos-de-caja', true),
  ];

  const columnasDePunto: Columna<PuntoDeVenta>[] = [
    {
      clave: 'numero',
      titulo: t('estructura.numero'),
      numerica: true,
      contenido: (fila) => fila.numero,
    },
    { clave: 'nombre', titulo: t('estructura.nombre'), contenido: (fila) => fila.nombre },
    columnaDeSucursal<PuntoDeVenta>(),
    {
      clave: 'modo',
      titulo: t('estructura.modo'),
      contenido: (fila) => t(`estructura.modos.${fila.modo}`, { defaultValue: fila.modo }),
    },
    columnaDeCorregir<PuntoDeVenta>('/puntos-de-venta', 'puntos-de-venta', false),
  ];

  return (
    <>
      <Encabezado titulo={t('estructura.titulo')} descripcion={t('estructura.descripcion')} />

      <div className="flex flex-col gap-4">
        <SeccionDeRecurso<Deposito>
          titulo={t('estructura.depositos')}
          descripcion={t('estructura.ayudaDepositos')}
          clave={['depositos']}
          ruta="/depositos"
          columnas={columnasDeDeposito}
          claveDeFila={(fila) => fila.id}
          textoVacio={t('estructura.sinDepositos')}
          puedeCrear={usePermiso('EMPRESA.DEPOSITO.CREAR')}
          tituloDelAlta={t('estructura.nuevoDeposito')}
          cuerpoDelAlta={() => depositoNuevo}
          alGuardar={() => {
            setDepositoNuevo({ codigo: '', nombre: '', sucursalId: '', esPredeterminado: false });
          }}
          formulario={
            <>
              <Selector
                etiqueta={t('estructura.sucursal')}
                obligatorio
                value={depositoNuevo.sucursalId}
                onChange={(evento) => {
                  setDepositoNuevo((previo) => ({ ...previo, sucursalId: evento.target.value }));
                }}
              >
                {opcionesDeSucursal}
              </Selector>
              <Campo
                etiqueta={t('estructura.codigo')}
                obligatorio
                value={depositoNuevo.codigo}
                onChange={(evento) => {
                  setDepositoNuevo((previo) => ({
                    ...previo,
                    codigo: evento.target.value.toUpperCase(),
                  }));
                }}
              />
              <Campo
                etiqueta={t('estructura.nombre')}
                obligatorio
                value={depositoNuevo.nombre}
                onChange={(evento) => {
                  setDepositoNuevo((previo) => ({ ...previo, nombre: evento.target.value }));
                }}
              />
            </>
          }
        />

        <SeccionDeRecurso<PuestoDeCaja>
          titulo={t('estructura.cajas')}
          descripcion={t('estructura.ayudaCajas')}
          clave={['puestos-de-caja']}
          ruta="/puestos-de-caja"
          columnas={columnasDeCaja}
          claveDeFila={(fila) => fila.id}
          textoVacio={t('estructura.sinCajas')}
          puedeCrear={usePermiso('EMPRESA.PUESTO_DE_CAJA.CREAR')}
          tituloDelAlta={t('estructura.nuevaCaja')}
          cuerpoDelAlta={() => cajaNueva}
          alGuardar={() => {
            setCajaNueva({ codigo: '', nombre: '', sucursalId: '' });
          }}
          formulario={
            <>
              <Selector
                etiqueta={t('estructura.sucursal')}
                obligatorio
                value={cajaNueva.sucursalId}
                onChange={(evento) => {
                  setCajaNueva((previo) => ({ ...previo, sucursalId: evento.target.value }));
                }}
              >
                {opcionesDeSucursal}
              </Selector>
              <Campo
                etiqueta={t('estructura.codigo')}
                obligatorio
                value={cajaNueva.codigo}
                onChange={(evento) => {
                  setCajaNueva((previo) => ({
                    ...previo,
                    codigo: evento.target.value.toUpperCase(),
                  }));
                }}
              />
              <Campo
                etiqueta={t('estructura.nombre')}
                obligatorio
                value={cajaNueva.nombre}
                onChange={(evento) => {
                  setCajaNueva((previo) => ({ ...previo, nombre: evento.target.value }));
                }}
              />
            </>
          }
        />

        <SeccionDeRecurso<PuntoDeVenta>
          titulo={t('estructura.puntosDeVenta')}
          descripcion={t('estructura.ayudaPuntosDeVenta')}
          clave={['puntos-de-venta']}
          ruta="/puntos-de-venta"
          columnas={columnasDePunto}
          claveDeFila={(fila) => fila.id}
          textoVacio={t('estructura.sinPuntosDeVenta')}
          puedeCrear={usePermiso('EMPRESA.PUNTO_DE_VENTA.CREAR')}
          tituloDelAlta={t('estructura.nuevoPuntoDeVenta')}
          cuerpoDelAlta={() => ({ ...puntoNuevo, numero: Number(puntoNuevo.numero) })}
          alGuardar={() => {
            setPuntoNuevo({ numero: '', nombre: '', sucursalId: '' });
          }}
          formulario={
            <>
              <Selector
                etiqueta={t('estructura.sucursal')}
                obligatorio
                value={puntoNuevo.sucursalId}
                onChange={(evento) => {
                  setPuntoNuevo((previo) => ({ ...previo, sucursalId: evento.target.value }));
                }}
              >
                {opcionesDeSucursal}
              </Selector>
              <Campo
                etiqueta={t('estructura.numero')}
                obligatorio
                inputMode="numeric"
                value={puntoNuevo.numero}
                ayuda={t('estructura.ayudaNumero')}
                onChange={(evento) => {
                  setPuntoNuevo((previo) => ({
                    ...previo,
                    numero: evento.target.value.replace(/\D/g, ''),
                  }));
                }}
              />
              <Campo
                etiqueta={t('estructura.nombre')}
                obligatorio
                value={puntoNuevo.nombre}
                onChange={(evento) => {
                  setPuntoNuevo((previo) => ({ ...previo, nombre: evento.target.value }));
                }}
              />
            </>
          }
        />
      </div>
      <CorregirNombre
        que={corrigiendo?.que ?? null}
        ruta={corrigiendo?.ruta ?? ''}
        conCodigo={corrigiendo?.conCodigo ?? true}
        alGuardar={[corrigiendo?.clave ?? '']}
        onCerrar={() => {
          setCorrigiendo(null);
        }}
      />
    </>
  );
}
